// Friendly names, groups and visible fields for each email template type.
export const TEMPLATE_LABELS: Record<string, string> = {
  user_invitation: 'User invitation',
  partner_invitation: 'Partner invitation',
  magic_link: 'Magic link sign-in',
  password_reset: 'Password reset',
  camera_alert: 'Camera alert',
  camera_image: 'New camera image',
  proposal_notification: 'Proposal notification',
  proposal_acceptance: 'Proposal acceptance',
  order_confirmation: 'Order confirmation',
  release_notification: 'Release notification',
  outage_alert: 'Outage alert',
  outage_resolved: 'Outage resolved',
};

export const TEMPLATE_GROUPS: { label: string; types: string[] }[] = [
  { label: 'Account & sign-in', types: ['user_invitation', 'partner_invitation', 'magic_link', 'password_reset'] },
  { label: 'Camera notifications', types: ['camera_alert', 'camera_image'] },
  { label: 'Sales & orders', types: ['proposal_notification', 'proposal_acceptance', 'order_confirmation'] },
  { label: 'System notices', types: ['release_notification', 'outage_alert', 'outage_resolved'] },
];

export const templateLabel = (type: string) => TEMPLATE_LABELS[type] || type.replace(/_/g, ' ');

export type ContentField =
  | 'greeting' | 'strapline' | 'intro' | 'description' | 'proposal_details' | 'benefits'
  | 'cta_text' | 'magic_link_text' | 'reset_link_text' | 'security_notice' | 'footer'
  | 'badge_text' | 'fallback_heading' | 'unsubscribe_text' | 'show_image';

const GENERIC: ContentField[] = ['greeting', 'strapline', 'intro', 'description', 'benefits', 'cta_text', 'footer'];

export const FIELDS_BY_TYPE: Record<string, ContentField[]> = {
  user_invitation: ['greeting', 'strapline', 'intro', 'description', 'benefits', 'cta_text', 'footer'],
  partner_invitation: ['greeting', 'strapline', 'intro', 'description', 'cta_text', 'footer'],
  proposal_notification: ['greeting', 'intro', 'description', 'proposal_details', 'benefits', 'cta_text', 'footer'],
  order_confirmation: ['greeting', 'intro', 'description', 'cta_text', 'footer'],
  magic_link: ['greeting', 'intro', 'description', 'magic_link_text', 'cta_text', 'security_notice', 'footer'],
  password_reset: ['greeting', 'intro', 'description', 'reset_link_text', 'cta_text', 'security_notice', 'footer'],
  release_notification: ['greeting', 'strapline', 'intro', 'description', 'cta_text', 'footer'],
  outage_alert: ['greeting', 'strapline', 'intro', 'description', 'cta_text', 'footer'],
  outage_resolved: ['greeting', 'strapline', 'intro', 'description', 'cta_text', 'footer'],
  camera_alert: ['badge_text', 'fallback_heading', 'intro', 'cta_text', 'footer', 'unsubscribe_text', 'show_image'],
  camera_image: ['badge_text', 'fallback_heading', 'intro', 'cta_text', 'footer', 'unsubscribe_text', 'show_image'],
};

/** Fields for a type, plus any other field that already holds a value (never hide saved content). */
export function fieldsFor(type: string, content: Record<string, any> | undefined): ContentField[] {
  const base = FIELDS_BY_TYPE[type] || GENERIC;
  const extra = Object.keys(content || {}).filter((k) => {
    const v = (content as any)[k];
    const has = Array.isArray(v) ? v.length > 0 : typeof v === 'string' ? v.trim() !== '' : false;
    return has && !base.includes(k as ContentField) && k in FIELD_META;
  }) as ContentField[];
  return [...base, ...extra];
}

export const FIELD_META: Record<ContentField, { label: string; multiline?: boolean; placeholder?: string; help?: string }> = {
  greeting: { label: 'Greeting', placeholder: 'Hi {{contactName}},' },
  strapline: { label: 'Strapline / subheading' },
  intro: { label: 'Introduction', multiline: true },
  description: { label: 'Description', multiline: true },
  proposal_details: { label: 'Proposal details', multiline: true },
  benefits: { label: 'Benefits (one per line)', multiline: true },
  cta_text: { label: 'Button text', placeholder: 'Get started' },
  magic_link_text: { label: 'Sign-in link text' },
  reset_link_text: { label: 'Reset link text' },
  security_notice: { label: 'Security notice', multiline: true },
  footer: { label: 'Footer', multiline: true },
  badge_text: { label: 'Badge text', placeholder: 'ALERT' },
  fallback_heading: { label: 'Heading when the AI has no subject', placeholder: 'Alert from {{camera_name}}' },
  unsubscribe_text: { label: 'Stop-alerts link text', placeholder: 'Stop these alerts' },
  show_image: { label: 'Show the camera image' },
};
